import React, { PureComponent, useRef } from 'react';

import { injectIntl, InjectedIntl, FormattedMessage } from 'react-intl';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import BitbucketBranchesIcon from '@atlaskit/icon/glyph/bitbucket/branches';
import LabelIcon from '@atlaskit/icon/glyph/label';
import SimpleTag from '@atlaskit/tag/simple-tag';
import Tooltip from '@atlaskit/tooltip';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import RenderedTitle from '@atlassian/bitbucket-rendered-title';
import { COMMIT_LIST_HEADER_HEIGHT } from '@atlassian/bitkit-commit-list';

import { ScreenReadersOnly } from 'src/components/accessibility';
import Announcer from 'src/components/announcer';
import {
  ComponentLink,
  ComponentLinkProxy,
} from 'src/components/component-link';
import { RenderProps } from 'src/components/list-keyboard-shortcuts';
import { TransparentLoadingCover } from 'src/components/pageable-table';
import { useClientSideLinking } from 'src/components/pipelines/hooks/use-client-side-links';
import { GpgKeysCommitVerification } from 'src/components/settings/gpg-keys/components/commit-verification';
import {
  Commit,
  Branch as BranchType,
  Tag as TagType,
  RenderedContent,
} from 'src/components/types';
import { CommitList } from 'src/containers/commit-list';
import { Link } from 'src/router/components';
import urls from 'src/sections/repository/urls';
import { CommitGraphElement } from 'src/utils/commit-graph-builder';
import { safeEncode } from 'src/utils/path';

import Pagination from '../containers/pagination';

import { CommitGraph, Graph } from './commit-graph';
import { CommitListBuildStatus } from './commit-list-build-status';
import { renderApproval } from './commit-list-icons';
import messages from './commit-list.i18n';
import * as styles from './commit-list.style';

const COMMIT_GRAPH_SLICE_HEIGHT = 40;
const COMMIT_GRAPH_COLUMN_WIDTH = 8;
const COMMIT_GRAPH_OFFSET_Y =
  COMMIT_LIST_HEADER_HEIGHT + COMMIT_GRAPH_SLICE_HEIGHT / 2;
const COMMIT_GRAPH_MAX_COLUMNS = 25;

export type CommitListProps = {
  commits: Commit[];
  commitGraphElements: CommitGraphElement[];
  currentPage: number;
  startPage: number;
  isLoading: boolean;
  nextUrl?: string;
  previousUrl?: string;
  isPreRequestState: boolean;
  isFilterApplied: boolean;
  repositoryFullSlug: string;
  newCommits: Commit[];
};

type IntlProps = {
  intl: InjectedIntl;
};

export const ClientSideLinksCommitMessage = ({
  renderedContent,
}: {
  renderedContent: RenderedContent;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useClientSideLinking(containerRef);

  // This is the default commit message rendering but with useClientSideLinking() hook
  return (
    <div ref={containerRef}>
      <RenderedTitle fadeLinesAfterFirst renderedContent={renderedContent} />
    </div>
  );
};

class PaginatedCommitList extends PureComponent<
  CommitListProps & RenderProps & IntlProps
> {
  renderRefs = (
    refs: BranchType[] | TagType[] | undefined,
    kind: 'branch' | 'tag',
    repositoryFullSlug: string
  ) => {
    if (!refs || !refs.length) {
      return null;
    }

    const message =
      kind === 'tag' ? messages.tagsTooltip : messages.branchesTooltip;

    const icon =
      kind === 'tag' ? (
        <LabelIcon label="" size="small" />
      ) : (
        <BitbucketBranchesIcon label="" size="small" />
      );
    const srOnlyLabel =
      kind === 'tag' ? messages.srOnlyTagName : messages.srOnlyBranchName;

    const { intl } = this.props;
    if (refs.length > 1) {
      // for some reason typescript complains when mapping over
      // BranchType[] | TagType[], although both these types
      // have `name` field
      const content = (refs as any[]).map(r => r.name).join(', ');
      return (
        <Tooltip content={content}>
          <SimpleTag
            elemBefore={icon}
            text={intl.formatMessage(message, { count: refs.length })}
          />
        </Tooltip>
      );
    }

    const [ref] = refs;
    const filterLink = urls.ui.commits(repositoryFullSlug, {
      kind,
      refName: safeEncode(ref.name),
    });
    const enableClientSideLinking = FeatureGates.checkGate(
      StatsigFeatureKeys.enableClientSideLinking
    );

    return (
      <Tooltip content={ref.name}>
        <styles.CommitListTag aria-hidden="true">
          <ScreenReadersOnly>
            {intl.formatMessage(srOnlyLabel)}
          </ScreenReadersOnly>
          <SimpleTag
            elemBefore={icon}
            href={filterLink}
            text={ref.name}
            linkComponent={enableClientSideLinking ? Link : undefined}
          />
        </styles.CommitListTag>
      </Tooltip>
    );
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  renderAfterMessage = commit => {
    const { extra = {} } = commit;
    const { branches, tags } = extra;
    const { intl } = this.props;
    return (
      <>
        {this.renderRefs(tags, 'tag', commit.repository.full_name)}
        {this.renderRefs(branches, 'branch', commit.repository.full_name)}
        {renderApproval(commit, intl)}
      </>
    );
  };

  renderCommitGraph() {
    const { isLoading, commitGraphElements, currentPage, startPage } =
      this.props;
    const hasPadding = currentPage > 1;

    if (startPage === 1) {
      return (
        <CommitGraph
          commitGraphElements={commitGraphElements}
          columnWidth={COMMIT_GRAPH_COLUMN_WIDTH}
          isLoading={isLoading}
          offsetX={COMMIT_GRAPH_COLUMN_WIDTH}
          offsetY={COMMIT_GRAPH_OFFSET_Y}
          sliceHeight={COMMIT_GRAPH_SLICE_HEIGHT}
        >
          {({ maxColumns, ...props }) =>
            maxColumns <= COMMIT_GRAPH_MAX_COLUMNS && (
              <styles.CommitGraphContainer>
                <Graph {...props} hasPadding={hasPadding} />
              </styles.CommitGraphContainer>
            )
          }
        </CommitGraph>
      );
    }

    return null;
  }

  renderBuildStatus = (commit: Commit) => {
    const { repositoryFullSlug } = this.props;
    return (
      <CommitListBuildStatus
        commit={commit}
        repositoryFullSlug={repositoryFullSlug}
      />
    );
  };

  renderVerificationStatus = (commit: Commit) => (
    <GpgKeysCommitVerification commit={commit} />
  );

  renderCommitMessage = (renderedContent: RenderedContent) => {
    return <ClientSideLinksCommitMessage renderedContent={renderedContent} />;
  };

  render() {
    const {
      isFilterApplied,
      isLoading,
      nextUrl,
      previousUrl,
      isPreRequestState,
      focusedRowIndex,
      commits,
      repositoryFullSlug,
      intl,
    } = this.props;
    const hasPages = !!nextUrl || !!previousUrl;
    const enableClientSideLinking = FeatureGates.checkGate(
      StatsigFeatureKeys.enableClientSideLinking
    );

    const renderPagination = () => (
      <styles.PaginationContainer data-testid="commit-list-pagination">
        <Pagination
          nextUrl={nextUrl}
          previousUrl={previousUrl}
          component={ComponentLinkProxy}
          uiEventSource="commitsScreen"
        />
      </styles.PaginationContainer>
    );

    const commitListProps = {
      commits,
      showComments: true,
      showHeaders: true,
      renderAfterMessage: this.renderAfterMessage,
      focusedRowIndex,
      renderBuildStatus: this.renderBuildStatus,
      renderCommitMessage: enableClientSideLinking
        ? this.renderCommitMessage
        : undefined,
      renderVerificationStatus: this.renderVerificationStatus,
      renderPagination: hasPages ? renderPagination : null,
      repositoryFullSlug,
    };

    if (
      !this.props.commits.length &&
      !isLoading &&
      this.props.isFilterApplied
    ) {
      return (
        <styles.EmptyResults>
          <Announcer text={intl.formatMessage(messages.emptyResultsHeading)} />
          <FormattedMessage tagName="h3" {...messages.emptyResultsHeading} />
          <FormattedMessage
            {...messages.emptyResultsInfo}
            values={{
              link: (
                <ComponentLink href={urls.ui.commits(repositoryFullSlug)}>
                  <FormattedMessage {...messages.emptyResultsLink} />
                </ComponentLink>
              ),
            }}
          />
        </styles.EmptyResults>
      );
    }

    return (
      <styles.CommitListContainer data-qa="commit-list-container">
        {isLoading && (
          <>
            <Announcer
              text={intl.formatMessage(messages.announcerFilterProcess)}
            />
            <TransparentLoadingCover />
          </>
        )}
        {this.renderCommitGraph()}
        {isLoading || isPreRequestState ? (
          <CommitList
            {...commitListProps}
            isLoading
            shouldShowLoadingCover={false}
            repositoryFullSlug={repositoryFullSlug}
          />
        ) : (
          <>
            {isFilterApplied ? (
              <Announcer
                text={intl.formatMessage(
                  hasPages && nextUrl
                    ? messages.announcerPaginatedFilterResults
                    : messages.announcerFilterResults,
                  {
                    count: commits.length,
                  }
                )}
              />
            ) : (
              <Announcer
                text={intl.formatMessage(messages.announceDisplayingAllCommits)}
              />
            )}
            <CommitList {...commitListProps} />
          </>
        )}
      </styles.CommitListContainer>
    );
  }
}

export default injectIntl(PaginatedCommitList);
